import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Info, X, Edit2, Trash2, Loader2, Calendar, Clock } from 'lucide-react';
import { cycleService, eventCycleService, Cycle, ApiResponse } from '../services/adminAuth';
import Swal from 'sweetalert2';
import { formatDateDisplay, formatDateForInput, formatInputDisplay, parseDateFromInput } from '../../../utils/dateHelper';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface EventCycleConfig {
    id: number;
    cycle: string;
    cycleId: number;
    event: string;
    eventId: number;
    status: boolean;
    startDate: string;
    endDate: string;
}

const EventCycle: React.FC = () => {
    const [showAddModal, setShowAddModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState<'activate' | 'deactivate'>('deactivate');
    const [selectedConfig, setSelectedConfig] = useState<EventCycleConfig | null>(null);
    const [editingConfig, setEditingConfig] = useState<EventCycleConfig | null>(null);

    const [showManageCyclesModal, setShowManageCyclesModal] = useState(false);
    const [cycles, setCycles] = useState<Cycle[]>([]);
    const [isCyclesLoading, setIsCyclesLoading] = useState(false);
    const [isSavingCycle, setIsSavingCycle] = useState(false);
    const [editingCycle, setEditingCycle] = useState<Cycle | null>(null);
    const [cycleName, setCycleName] = useState('');
    const [cycleTag, setCycleTag] = useState('');
    const [isTogglingStatus, setIsTogglingStatus] = useState(false);
    const [isEnablingCycleId, setIsEnablingCycleId] = useState<number | null>(null);
    const [configurations, setConfigurations] = useState<EventCycleConfig[]>([]);
    const [isConfigLoading, setIsConfigLoading] = useState(false);
    const [isSavingConfig, setIsSavingConfig] = useState(false);
    const [eventTypes, setEventTypes] = useState<any[]>([]);
    const [filterCycles, setFilterCycles] = useState<Cycle[]>([]);
    const [selectedFilterCycleId, setSelectedFilterCycleId] = useState<number | null>(null);

    const [formData, setFormData] = useState({
        cycleId: '',
        eventId: '',
        startDate: '',
        endDate: '',
    });

    useEffect(() => {
        fetchCycles();
        fetchEventTypes();
        loadFilterCycles();
    }, []);

    useEffect(() => {
        const initializeData = async () => {
            await fetchEventTypes();
            // Load all cycles for the dropdown and get the active one
            const activeCycle = await loadFilterCycles();

            // If an active cycle was found, set it as the default filter.
            if (activeCycle) {
                setSelectedFilterCycleId(activeCycle.id);
            }
        };

        initializeData();
    }, []);

    const fetchEventTypes = async () => {
        try {
            const response = await eventCycleService.getAllEventTypes(true);
            if (response.isSuccess) {
                setEventTypes(response.data);
            } else {
                Swal.fire('Error', response.message || 'Failed to fetch event types', 'error');
            }
        } catch (error) {
            Swal.fire('Error', 'An unexpected error occurred while fetching event types', 'error');
        }
    };

    const loadFilterCycles = async (): Promise<Cycle | null> => {
        try {
            const allRes = await cycleService.getAllCycles(true);
            let allCycles: Cycle[] = [];

            if (allRes.isSuccess) {
                allCycles = allRes.data;
                setFilterCycles(allCycles);
            }


            const activeByStatus = allCycles.find(c => c.submissionReady);
            if (activeByStatus) return activeByStatus;

            const activeEventRes = await eventCycleService.getActiveCycleEvent();
            if (activeEventRes.isSuccess && activeEventRes.data?.cycle) {
                return activeEventRes.data.cycle;
            }

            return allCycles.length > 0 ? allCycles[0] : null;

        } catch (err) {
            return null;
        }
    };
    const fetchEventCycles = async (cycleId?: number) => {
        const id = cycleId ?? selectedFilterCycleId;
        if (!id) return;
        setIsConfigLoading(true);
        try {
            const response = await eventCycleService.getAllEventCycles(id);
            if (response.isSuccess) {
                const mappedResults = response.data.map((item: any) => {
                    const cycleName = item.cycle?.name || cycles.find(c => c.id === item.cycleId)?.name || 'N/A';
                    const eventTypeName = item.eventType?.name || eventTypes.find(e => e.id === item.eventTypeId)?.name || 'N/A';

                    return {
                        id: item.id,
                        cycle: cycleName,
                        cycleId: item.cycleId,
                        event: eventTypeName,
                        eventId: item.eventTypeId,
                        status: item.active,
                        startDate: item.startDate || '',
                        endDate: item.endDate || ''
                    };
                });
                setConfigurations(mappedResults);
            } else {
                Swal.fire('Error', response.message || 'Failed to fetch event cycles', 'error');
            }
        } catch (error) {
            Swal.fire('Error', 'An unexpected error occurred while fetching event cycles', 'error');
        } finally {
            setIsConfigLoading(false);
        }
    };

    useEffect(() => {
        if (!selectedFilterCycleId) return;
        fetchEventCycles(selectedFilterCycleId);
    }, [selectedFilterCycleId]);

    const fetchCycles = async () => {
        setIsCyclesLoading(true);
        try {
            const response = await cycleService.getAllCycles(true);
            if (response.isSuccess) {
                setCycles(response.data);
            } else {
                Swal.fire('Error', response.message || 'Failed to fetch cycles', 'error');
            }
        } catch (error) {
            Swal.fire('Error', 'An unexpected error occurred while fetching cycles', 'error');
        } finally {
            setIsCyclesLoading(false);
        }
    };

    const handleSaveCycle = async () => {
        if (!cycleName.trim()) {
            Swal.fire('Error', 'Cycle name is required', 'error');
            return;
        }

        setIsSavingCycle(true);
        try {
            let response: ApiResponse<Cycle | any>;
            if (editingCycle) {
                response = await cycleService.updateCycle({
                    ...editingCycle,
                    name: cycleName,
                    tag: cycleTag
                });
            } else {
                if (cycleTag.length > 2) {
                    Swal.fire('Error', 'Tag cannot be more than 2 characters', 'error');
                    setIsSavingCycle(false);
                    return;
                }
                response = await cycleService.createCycle(cycleName, cycleTag);
            }

            if (response.isSuccess) {
                Swal.fire('Success', response.message || 'Operation successful', 'success');
                setCycleName('');
                setEditingCycle(null);
                await fetchCycles();
                const defaultCycle = await loadFilterCycles();
                if (defaultCycle && selectedFilterCycleId === null) {
                    setSelectedFilterCycleId(defaultCycle.id);
                    fetchEventCycles(defaultCycle.id);
                }
            } else {
                Swal.fire('Error', response.message || 'Operation failed', 'error');
            }
        } catch (error) {
            Swal.fire('Error', 'An unexpected error occurred', 'error');
        } finally {
            setIsSavingCycle(false);
        }
    };

    const handleDeleteCycle = async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const response = await cycleService.deleteCycle(id);
                if (response.isSuccess) {
                    Swal.fire('Deleted!', 'Cycle has been deleted.', 'success');
                    fetchCycles();
                } else {
                    Swal.fire('Error', response.message || 'Delete failed', 'error');
                }
            } catch (error) {
                Swal.fire('Error', 'An unexpected error occurred', 'error');
            }
        }
    };

    const startEditCycle = (cycle: Cycle) => {
        setEditingCycle(cycle);
        setCycleName(cycle.name);
        setCycleTag(cycle.tag || '');
    };

    const handleEnableCycle = async (cycle: Cycle) => {
        if (cycle.submissionReady) return; // Already active

        const result = await Swal.fire({
            title: 'Enable Cycle for Activities?',
            html: `This will set <strong>${cycle.name}</strong> as the active cycle and disable all others.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#16a34a',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, enable it'
        });

        if (result.isConfirmed) {
            setIsEnablingCycleId(cycle.id);
            try {
                const response = await cycleService.enableCycleForActivities(cycle.id);
                if (response.isSuccess) {
                    Swal.fire('Success', response.message || `${cycle.name} is now the active cycle`, 'success');
                    await fetchCycles();
                    const activeCycle = await loadFilterCycles(); // Refresh the filter dropdown
                    if (activeCycle) {
                        setSelectedFilterCycleId(activeCycle.id);
                        fetchEventCycles(activeCycle.id);
                    }
                    window.dispatchEvent(new Event('activeCycleUpdated'));
                } else {
                    Swal.fire('Error', response.message || 'Failed to enable cycle', 'error');
                }
            } catch (error) {
                Swal.fire('Error', 'An unexpected error occurred', 'error');
            } finally {
                setIsEnablingCycleId(null);
            }
        }
    };

    const handleToggleStatus = (config: EventCycleConfig) => {
        setSelectedConfig(config);
        setConfirmAction(config.status ? 'deactivate' : 'activate');
        setShowConfirmModal(true);
    };

    const handleConfirmToggle = async () => {
        if (selectedConfig) {
            // QA Issue Fix: Prevent activation if end duration has passed
            if (confirmAction === 'activate' && selectedConfig.endDate) {
                const endDate = new Date(selectedConfig.endDate);
                if (endDate < new Date()) {
                    Swal.fire('Error', 'Cannot activate an event that has already ended.', 'error');
                    setShowConfirmModal(false);
                    setSelectedConfig(null);
                    return;
                }
            }

            setIsTogglingStatus(true);
            try {
                let response: ApiResponse<any>;
                // The API now uses activateEventCycle for both toggling on and off
                // Pass true to activate, false to deactivate
                response = await eventCycleService.activateEventCycle(
                    selectedConfig.id,
                    !selectedConfig.status
                );

                if (response.isSuccess) {
                    Swal.fire('Success', response.message || 'Status updated successfully', 'success');
                    await fetchEventCycles(); // Wait for fetch to complete
                } else {
                    Swal.fire('Error', response.message || 'Update failed', 'error');
                }
            } catch (error) {
                Swal.fire('Error', 'An unexpected error occurred', 'error');
            } finally {
                setIsTogglingStatus(false);
            }
        }
        setShowConfirmModal(false);
        setSelectedConfig(null);
    };

    const handleEdit = (config: EventCycleConfig) => {
        setEditingConfig(config);
        setFormData({
            cycleId: config.cycleId.toString(),
            eventId: config.eventId.toString(),
            startDate: formatDateForInput(config.startDate),
            endDate: formatDateForInput(config.endDate),
        });
        setShowAddModal(true);
    };

    const handleDelete = async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const response = await eventCycleService.deleteEventCycle(id);
                if (response.isSuccess) {
                    Swal.fire('Deleted!', 'Event configuration has been deleted.', 'success');
                    fetchEventCycles();
                } else {
                    Swal.fire('Error', response.message || 'Delete failed', 'error');
                }
            } catch (error) {
                Swal.fire('Error', 'An unexpected error occurred', 'error');
            }
        }
    };

    const handleSaveConfiguration = async () => {
        if (!formData.cycleId || !formData.eventId || !formData.startDate || !formData.endDate) {
            Swal.fire('Error', 'All fields are required', 'error');
            return;
        }

        setIsSavingConfig(true);
        try {
            const payload = {
                id: editingConfig ? editingConfig.id : 0,
                eventTypeId: parseInt(formData.eventId),
                cycleId: parseInt(formData.cycleId),
                startDate: parseDateFromInput(formData.startDate),
                endDate: parseDateFromInput(formData.endDate)
            };

            let response: ApiResponse<any>;
            if (editingConfig) {
                response = await eventCycleService.updateEventCycle(payload);
            } else {
                response = await eventCycleService.createEventCycle(payload);
            }

            if (response.isSuccess || response.message?.toLowerCase().includes('success')) {
                Swal.fire('Success', response.message || 'Configuration saved successfully', 'success');
                handleCloseModal();
                // QA Issue Fix: Ensure list is refreshed after save
                setTimeout(() => {
                    fetchEventCycles();
                }, 500);
            } else {
                Swal.fire('Error', response.message || 'Operation failed', 'error');
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
            Swal.fire('Error', errorMessage, 'error');
        } finally {
            setIsSavingConfig(false);
        }
    };

    const handleCloseModal = () => {
        setShowAddModal(false);
        setEditingConfig(null);
        setFormData({
            cycleId: '',
            eventId: '',
            startDate: '',
            endDate: '',
        });
    };

    return (
        <div className="w-full">
            <div className="mb-6 flex justify-between items-start">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Event Cycle Settings</h1>
                    <p className="text-gray-500">Configure which events are active for each operational cycle.</p>
                </div>
                {/* <button className="px-4 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center gap-2">
                    Save Changes
                </button> */}
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                <div className="mb-6 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">

                    <div>
                        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
                            Configuration Matrix
                        </h2>
                        <p className="text-sm sm:text-base text-gray-500">
                            Manage event availability per cycle.
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                        <div className="relative w-full sm:w-52">
                            <select
                                value={selectedFilterCycleId ?? ''}
                                onChange={(e) => {
                                    const id = Number(e.target.value);
                                    setSelectedFilterCycleId(id);
                                    fetchEventCycles(id);
                                }}
                                className="w-full px-4 py-2 pr-10 bg-gray-50 border border-gray-300 rounded-lg text-gray-700 text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 appearance-none"
                                style={{
                                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'right 0.75rem center',
                                    backgroundSize: '16px'
                                }}
                            >
                                <option value="" disabled>Select cycle</option>
                                {filterCycles.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>

                        <button
                            onClick={() => setShowManageCyclesModal(true)}
                            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                            <Plus size={18} />
                            Add New Cycle
                        </button>

                        <button
                            onClick={() => setShowAddModal(true)}
                            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            <Plus size={18} />
                            Add New Event Cycle
                        </button>
                    </div>

                </div>


                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200">
                                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Cycle</th>
                                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Event</th>
                                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Status</th>
                                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Duration (Start & End)</th>
                                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!selectedFilterCycleId ? (
                                <tr>
                                    <td colSpan={5} className="py-16 text-center">
                                        <div className="flex flex-col items-center gap-2">
                                            <Info size={28} className="text-gray-300" />
                                            <p className="text-gray-500 text-sm">Select a cycle from the dropdown above to view its event configurations.</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : isConfigLoading ? (
                                <tr>
                                    <td colSpan={5} className="py-12 text-center">
                                        <Loader2 className="animate-spin text-green-600 mx-auto" size={32} />
                                        <p className="text-sm text-gray-500 mt-2">Loading configurations...</p>
                                    </td>
                                </tr>
                            ) : configurations.length > 0 ? (
                                configurations.map((config) => (
                                    <tr key={config.id} className="border-b border-gray-100 hover:bg-gray-50">
                                        <td className="py-4 px-4">
                                            <span className="text-sm text-gray-900">{config.cycle}</span>
                                        </td>
                                        <td className="py-4 px-4">
                                            <span className="text-sm text-gray-900">{config.event}</span>
                                        </td>
                                        <td className="py-4 px-4">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handleToggleStatus(config)}
                                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.status ? 'bg-green-600' : 'bg-gray-300'
                                                        }`}
                                                >
                                                    <span
                                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.status ? 'translate-x-6' : 'translate-x-1'
                                                            }`}
                                                    />
                                                </button>
                                                <span className={`text-sm font-medium ${config.status ? 'text-green-600' : 'text-gray-500'}`}>
                                                    {config.status ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-4 px-4">
                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                <span>{formatDateDisplay(config.startDate)}</span>
                                                <span className="text-gray-400">-</span>
                                                <span>{formatDateDisplay(config.endDate)}</span>
                                            </div>
                                        </td>
                                        <td className="py-4 px-4">
                                            <div className="flex items-center gap-4">
                                                <button
                                                    onClick={() => handleEdit(config)}
                                                    className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(config.id)}
                                                    className="text-red-600 hover:text-red-800 font-medium text-sm transition-colors"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="py-12 text-center text-gray-500">
                                        No configurations found for this cycle. Add one to get started.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-1">About Event Cycles</h3>
                    <p className="text-sm text-blue-900">
                        Activating an event for a specific cycle will make it visible to eligible users in that cycle. Ensure dates are configured in the global cycle settings before activating events here.
                    </p>
                </div>
            </div>

            {showAddModal && (
                <>
                    <div
                        className="fixed inset-0 bg-black/50 z-40"
                        onClick={handleCloseModal}
                    />
                    <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 relative">
                            <button
                                onClick={handleCloseModal}
                                className="absolute top-6 right-6 text-gray-400 hover:text-gray-600"
                            >
                                <X size={24} />
                            </button>

                            <h2 className="text-3xl font-bold text-gray-900 mb-2">
                                {editingConfig ? 'Edit Cycle Configuration' : 'Add New Cycle Configuration'}
                            </h2>
                            <p className="text-gray-500 mb-8">Configure a new event for a cycle. Click save when you're done.</p>

                            <div className="space-y-6">
                                <div className="flex items-center gap-8">
                                    <label className="text-lg font-semibold text-gray-900 w-32">Cycle</label>
                                    <div className="flex-1">
                                        <select
                                            value={formData.cycleId}
                                            onChange={(e) => setFormData({ ...formData, cycleId: e.target.value })}
                                            className="w-full px-4 py-3 bg-gray-50 border-0 rounded-lg text-gray-900 cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500"
                                        >
                                            <option value="">Select cycle</option>
                                            {cycles.filter(c => c.active).map(c => (
                                                <option key={c.id} value={c.id.toString()}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="flex items-center gap-8">
                                    <label className="text-lg font-semibold text-gray-900 w-32">Event</label>
                                    <div className="flex-1">
                                        <select
                                            value={formData.eventId}
                                            onChange={(e) => setFormData({ ...formData, eventId: e.target.value })}
                                            className="w-full px-4 py-3 bg-gray-50 border-0 rounded-lg text-gray-900 cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500"
                                        >
                                            <option value="">Select event</option>
                                            {eventTypes.map(e => (
                                                <option key={e.id} value={e.id.toString()}>{e.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="flex items-center gap-8">
                                    <label className="text-lg font-semibold text-gray-900 w-32 flex items-center gap-2">
                                        Start Date
                                    </label>
                                    <div className="flex-1">
                                        <DatePicker
                                            selected={formData.startDate ? new Date(formData.startDate) : null}
                                            onChange={(date: Date | null) => setFormData({ ...formData, startDate: date ? formatDateForInput(date.toISOString()) : '' })}
                                            showTimeSelect
                                            timeFormat="HH:mm"
                                            timeIntervals={15}
                                            dateFormat="dd/MM/yyyy h:mm aa"
                                            minDate={new Date()}
                                            className="w-full px-4 py-3 bg-gray-50 border-0 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500"
                                            placeholderText="DD/MM/YYYY HH:MM"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center gap-8">
                                    <label className="text-lg font-semibold text-gray-900 w-32 flex items-center gap-2">
                                        End Date
                                    </label>
                                    <div className="flex-1">
                                        <DatePicker
                                            selected={formData.endDate ? new Date(formData.endDate) : null}
                                            onChange={(date: Date | null) => setFormData({ ...formData, endDate: date ? formatDateForInput(date.toISOString()) : '' })}
                                            showTimeSelect
                                            timeFormat="HH:mm"
                                            timeIntervals={15}
                                            dateFormat="dd/MM/yyyy h:mm aa"
                                            minDate={new Date()}
                                            className="w-full px-4 py-3 bg-gray-50 border-0 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500"
                                            placeholderText="DD/MM/YYYY HH:MM"
                                        />
                                    </div>
                                </div>
                                {/* 
                                <div className="flex items-center gap-8">
                                    <label className="text-lg font-semibold text-gray-900 w-32">Active</label>
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                                            className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${formData.isActive ? 'bg-green-600' : 'bg-gray-300'
                                                }`}
                                        >
                                            <span
                                                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${formData.isActive ? 'translate-x-7' : 'translate-x-1'
                                                    }`}
                                            />
                                        </button>
                                        <span className="text-gray-500">{formData.isActive ? 'Enabled' : 'Disabled'}</span>
                                    </div>
                                </div> */}
                            </div>

                            <div className="flex justify-end gap-4 mt-10">
                                <button
                                    onClick={handleCloseModal}
                                    className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveConfiguration}
                                    disabled={isSavingConfig}
                                    className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                                >
                                    {isSavingConfig && <Loader2 className="animate-spin" size={18} />}
                                    {editingConfig ? 'Update Configuration' : 'Save Configuration'}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {showConfirmModal && selectedConfig && (
                <>
                    <div
                        className="fixed inset-0 bg-black/50 z-40"
                        onClick={() => setShowConfirmModal(false)}
                    />
                    <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 relative">
                            <button
                                onClick={() => setShowConfirmModal(false)}
                                className="absolute top-6 right-6 text-gray-400 hover:text-gray-600"
                            >
                                <X size={24} />
                            </button>

                            <h2 className="text-3xl font-bold text-gray-900 mb-4">Confirm Action</h2>
                            <p className="text-gray-600 text-lg leading-relaxed">
                                Are you sure you want to {confirmAction === 'activate' ? 'activate' : 'deactivate'}{' '}
                                <span className="font-semibold text-gray-900">{selectedConfig.event}</span> for{' '}
                                <span className="font-semibold text-gray-900">{selectedConfig.cycle}</span>?
                                {confirmAction === 'deactivate'
                                    ? ' This will hide the event from all users immediately.'
                                    : ' This will make the event visible to all users immediately.'}
                            </p>

                            <div className="flex justify-end gap-4 mt-8">
                                <button
                                    onClick={() => setShowConfirmModal(false)}
                                    className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmToggle}
                                    disabled={isTogglingStatus}
                                    className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 ${confirmAction === 'activate'
                                        ? 'bg-green-600 text-white hover:bg-green-700'
                                        : 'bg-red-600 text-white hover:bg-red-700'
                                        }`}
                                >
                                    {isTogglingStatus && <Loader2 className="animate-spin" size={18} />}
                                    {confirmAction === 'activate' ? 'Activate Event' : 'Deactivate Event'}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {showManageCyclesModal && (
                <>
                    <div
                        className="fixed inset-0 bg-black/50 z-40"
                        onClick={() => {
                            setShowManageCyclesModal(false);
                            setEditingCycle(null);
                            setCycleName('');
                        }}
                    />
                    <div className="fixed inset-0 flex items-center justify-center z-[60] p-4">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-8 relative max-h-[90vh] overflow-y-auto">
                            <button
                                onClick={() => {
                                    setShowManageCyclesModal(false);
                                    setEditingCycle(null);
                                    setCycleName('');
                                }}
                                className="absolute top-6 right-6 text-gray-400 hover:text-gray-600"
                            >
                                <X size={24} />
                            </button>

                            <h2 className="text-3xl font-bold text-gray-900 mb-2">Manage Cycles</h2>
                            <p className="text-gray-500 mb-8">Add, update, or remove operational cycles.</p>

                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                <div className="lg:col-span-1 border-r border-gray-100 pr-8">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                        {editingCycle ? 'Update Cycle' : 'Add New Cycle'}
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Cycle Name</label>
                                            <input
                                                type="text"
                                                value={cycleName}
                                                onChange={(e) => setCycleName(e.target.value)}
                                                placeholder="e.g. Cycle 1 (Q1)"
                                                className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Cycle Tag (Max 2 chars)</label>
                                            <input
                                                type="text"
                                                value={cycleTag}
                                                maxLength={2}
                                                onChange={(e) => setCycleTag(e.target.value.toUpperCase())}
                                                placeholder="e.g. C1"
                                                className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                            />
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={handleSaveCycle}
                                                disabled={isSavingCycle}
                                                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                                            >
                                                {isSavingCycle ? (
                                                    <Loader2 className="animate-spin" size={18} />
                                                ) : (
                                                    editingCycle ? 'Update' : 'Add Cycle'
                                                )}
                                            </button>
                                            {editingCycle && (
                                                <button
                                                    onClick={() => {
                                                        setEditingCycle(null);
                                                        setCycleName('');
                                                    }}
                                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                                >
                                                    Cancel
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="lg:col-span-2">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Cycles</h3>
                                    {isCyclesLoading ? (
                                        <div className="flex items-center justify-center py-12">
                                            <Loader2 className="animate-spin text-green-600" size={32} />
                                        </div>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full">
                                                <thead>
                                                    <tr className="border-b border-gray-200">
                                                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">S/N</th>
                                                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Name</th>
                                                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Tag</th>
                                                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Active</th>
                                                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Status</th>
                                                        <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {cycles.length > 0 ? cycles.map((cycle, index) => (
                                                        <tr key={cycle.id} className="border-b border-gray-50 hover:bg-gray-50">
                                                            <td className="py-3 px-4 text-sm text-gray-600">{index + 1}</td>
                                                            <td className="py-3 px-4 text-sm text-gray-900 font-medium">{cycle.name}</td>
                                                            <td className="py-3 px-4 text-sm text-gray-600">{cycle.tag || 'N/A'}</td>
                                                            <td className="py-3 px-4">
                                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${cycle.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                                    {cycle.active ? 'Yes' : 'No'}
                                                                </span>
                                                            </td>
                                                            <td className="py-3 px-4">
                                                                <div className="flex items-center gap-2">
                                                                    <button
                                                                        onClick={() => handleEnableCycle(cycle)}
                                                                        disabled={isEnablingCycleId === cycle.id}
                                                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${cycle.submissionReady ? 'bg-green-600' : 'bg-gray-300'} ${isEnablingCycleId === cycle.id ? 'opacity-50' : ''}`}
                                                                    >
                                                                        {isEnablingCycleId === cycle.id ? (
                                                                            <Loader2 className="animate-spin mx-auto text-white" size={14} />
                                                                        ) : (
                                                                            <span
                                                                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${cycle.submissionReady ? 'translate-x-6' : 'translate-x-1'}`}
                                                                            />
                                                                        )}
                                                                    </button>
                                                                    <span className={`text-xs font-medium ${cycle.submissionReady ? 'text-green-600' : 'text-gray-500'}`}>
                                                                        {cycle.submissionReady ? 'Ready' : 'Not Ready'}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td className="py-3 px-4 text-right">
                                                                <div className="flex justify-end gap-3">
                                                                    <button
                                                                        onClick={() => startEditCycle(cycle)}
                                                                        className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                                        title="Edit"
                                                                    >
                                                                        <Edit2 size={16} />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDeleteCycle(cycle.id)}
                                                                        className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                                                        title="Delete"
                                                                    >
                                                                        <Trash2 size={16} />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )) : (
                                                        <tr>
                                                            <td colSpan={6} className="py-8 text-center text-gray-500">
                                                                No cycles found. Add one on the left.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default EventCycle;